import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
} from 'react';

import { router, usePage } from '@inertiajs/react';

import { Routes } from '../models/routes';
import { CurrentUser, Offer, ShippingMethod } from '../types';
import { formatLocalDate } from '../utils';

export interface CartItem {
    id: string | number;
    name: string;
    price: number;
    image: string;
    imageAlt?: string;
    quantity: number;
    offerId?: number;
    sellerId?: number;
    sellerName?: string;
    sellerShippingMethods?: ShippingMethod[];
    maxQuantity: number;
    condition?: string;
    propertySet?: Record<string, string>;
    productTypeId?: number;
}

interface CartContext {
    cartItems: CartItem[];
    removeFromCart: (id: string | number) => void;
    updateQuantity: (id: string | number, quantity: number) => void;
    clearCart: () => void;
    cartCount: number;
    totalPrice: number;
    isOwnOffer: (offer: Offer) => boolean;
    canAddToCart: (offer: Offer) => boolean;
    canAddQuantity: (offer: Offer, desiredQuantity: number) => boolean;
    updateOfferQuantity: (offerId: number, quantity: number) => void;
    getOfferQuantity: (offerId: number) => number;
    handleAddToCart: (offer: Offer) => void;
    getTooltipMessage: (offer: Offer) => string;
}

type PageProps = {
    currentUser: CurrentUser;
    offers: Offer[];
};

const CartContext = createContext<CartContext | undefined>(undefined);
const CART_STORAGE_KEY = 'cartItems';

const readCartFromStorage = (): CartItem[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const storedCart = window.localStorage.getItem(CART_STORAGE_KEY);
        if (!storedCart) {
            return [];
        }

        const parsedCart = JSON.parse(storedCart);
        return Array.isArray(parsedCart) ? parsedCart : [];
    } catch {
        return [];
    }
};

const writeCartToStorage = (items: CartItem[]) => {
    if (typeof window === 'undefined') {
        return;
    }

    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

const subscribeToCartChanges = (onChange: (items: CartItem[]) => void) => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const handleStorageChange = (event: StorageEvent) => {
        if (event.key !== CART_STORAGE_KEY) {
            return;
        }

        onChange(readCartFromStorage());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
};

export function CartProvider({ children }: { children: ReactNode }) {
    const [cartItems, setCartItems] = useState<CartItem[]>(() =>
        readCartFromStorage(),
    );
    const [offerQuantities, setOfferQuantities] = useState<
        Record<number, number>
    >({});
    const { currentUser, offers } = usePage<PageProps>().props;

    const commitCartMutation = useCallback(
        (updater: (currentItems: CartItem[]) => CartItem[]) => {
            const currentItems = readCartFromStorage();
            const nextItems = updater(currentItems);

            writeCartToStorage(nextItems);
            setCartItems(nextItems);
        },
        [],
    );

    useEffect(() => subscribeToCartChanges(setCartItems), []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const syncCart = () => {
            setCartItems(readCartFromStorage());
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncCart();
            }
        };

        window.addEventListener('focus', syncCart);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', syncCart);
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    const getOfferQuantity = (offerId: number) => {
        return offerQuantities[offerId] || 1;
    };

    // Update quantity for specific offer
    const updateOfferQuantity = (offerId: number, quantity: number) => {
        const offer = offers.find((o) => o.id === offerId);
        if (!offer) return;

        const maxQuantity = offer.amount;
        const existingItem = cartItems.find((item) => item.id === offerId);

        // Якщо товар вже є в кошику, враховуємо його кількість
        const availableQuantity = existingItem
            ? maxQuantity - existingItem.quantity
            : maxQuantity;

        const newQuantity = Math.max(1, Math.min(quantity, availableQuantity));
        setOfferQuantities((prev) => ({
            ...prev,
            [offerId]: newQuantity,
        }));
    };

    const addToCart = (item: Omit<CartItem, 'quantity'>, quantity = 1) => {
        commitCartMutation((currentItems) => {
            const existingItem = currentItems.find((i) => i.id === item.id);
            if (existingItem) {
                const newQuantity = existingItem.quantity + quantity;
                const finalQuantity = Math.min(newQuantity, item.maxQuantity);

                return currentItems.map((i) =>
                    i.id === item.id ? { ...i, quantity: finalQuantity } : i,
                );
            }
            return [
                ...currentItems,
                {
                    ...item,
                    quantity: Math.max(1, Math.min(quantity, item.maxQuantity)),
                },
            ];
        });
    };

    const handleAddToCart = (offer: Offer) => {
        if (!currentUser) {
            router.get(Routes.SignIn);
            return;
        }

        if (!canAddToCart(offer)) {
            return;
        }

        const quantity = getOfferQuantity(offer.id);
        addToCart(
            {
                id: offer.id,
                name: `${offer.product.name} (від ${offer.seller_name})`,
                price: offer.price,
                image: offer.product.image,
                imageAlt: offer.product.image_alt,
                offerId: offer.id,
                sellerId: offer.seller_id,
                sellerName: offer.seller_name,
                sellerShippingMethods: offer.seller_shipping_methods,
                maxQuantity: offer.amount,
                condition: offer.property_set['condition'],
                propertySet: { ...offer.property_set },
                productTypeId: offer.product.product_type_id,
            },
            quantity,
        );
    };

    const removeFromCart = (id: string | number) => {
        commitCartMutation((currentItems) =>
            currentItems.filter((item) => item.id !== id),
        );
    };

    const updateQuantity = (id: string | number, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(id);
            return;
        }
        commitCartMutation((currentItems) =>
            currentItems.map((item) =>
                item.id === id
                    ? {
                          ...item,
                          quantity: Math.max(
                              1,
                              Math.min(quantity, item.maxQuantity),
                          ),
                      }
                    : item,
            ),
        );
    };

    const clearCart = () => {
        writeCartToStorage([]);
        setCartItems([]);
    };

    const cartCount = cartItems.reduce(
        (count, item) => count + item.quantity,
        0,
    );
    const totalPrice = cartItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0,
    );

    const isOwnOffer = (offer: Offer) => {
        return offer.user.id === currentUser?.id;
    };

    const sellerPaused = (offer: Offer) =>
        Boolean(offer.seller_paused || offer.user.seller_paused);

    const sellerPausedUntil = (offer: Offer) =>
        offer.seller_paused_until || offer.user.seller_paused_until;

    // Check if offer can be added to cart based on seller restrictions
    const canAddToCart = (offer: Offer) => {
        if (!currentUser) {
            return true;
        }

        if (isOwnOffer(offer)) {
            return false; // Can't add own offers
        }

        return !sellerPaused(offer);
    };

    // Check if we can add the desired quantity to cart
    const canAddQuantity = (offer: Offer, desiredQuantity: number) => {
        if (!currentUser) {
            return true;
        }

        const existingItem = cartItems.find((item) => item.id === offer.id);

        if (!existingItem) {
            // If item is not in cart, check if desired quantity doesn't exceed offer amount
            return desiredQuantity <= offer.amount;
        }

        // If item is already in cart, check if adding desired quantity doesn't exceed offer amount
        const newTotalQuantity = existingItem.quantity + desiredQuantity;
        return newTotalQuantity <= offer.amount;
    };

    const getTooltipMessage = (offer: Offer) => {
        if (isOwnOffer(offer)) {
            return 'Ви не можете додати власну пропозицію в кошик';
        }

        if (sellerPaused(offer)) {
            const pausedUntil = sellerPausedUntil(offer);

            return pausedUntil
                ? `Продавець тимчасово не приймає замовлення до ${formatLocalDate(pausedUntil)}`
                : 'Продавець тимчасово не приймає замовлення';
        }

        if (!canAddQuantity(offer, 1)) {
            return 'Ви вже додали максимальну кількість товару цієї пропозиції у кошик';
        }

        return '';
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                removeFromCart,
                updateQuantity,
                clearCart,
                cartCount,
                totalPrice,
                isOwnOffer,
                canAddToCart,
                canAddQuantity,
                updateOfferQuantity,
                handleAddToCart,
                getOfferQuantity,
                getTooltipMessage,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
